import React, { useContext, useEffect, useMemo, useRef } from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';

import get from 'lodash/get';
import Placeholder from './Placeholder';
import CollapsedBar from '../CollapsedBar';
import Loader from '../../../common/Loader';
import PublisherBtns from '../PublisherBtns';
import { OTContext } from '../../../../redux/opentok/context';
import { ConnectionShape } from '../../../../redux/opentok/helpers/shapes';
import { useDragVideo } from '../../../../redux/opentok/hooks/useDragVideo';
import { useVirtualVisit } from '../../../../redux/opentok/hooks/useVirtualVisit';
import styles from './styles.styl';
import { SCREEN_SHARING_VIDEO_TYPE } from '../../../../redux/opentok/helpers/constants';

function ActiveSpeaker({ publisher, coords, onPin }) {
  const { state, focusedId, PUBLISHER_KEY } = useContext(OTContext);
  const { loading, connections } = state;
  const { viewOptions, placeholder } = useVirtualVisit();
  const { collapsed, fullScreenView } = viewOptions;
  const { isDragging, drag } = useDragVideo(coords, collapsed);

  const isMe = focusedId === PUBLISHER_KEY;
  const moreOneUserInRoom = Object.keys(connections).length > 1;
  const activeRef = useRef();

  const sharingConnectionId = useMemo(() => {
    const sharingConnection = Object.values(connections).find(
      connection =>
        get(connection, 'stream.videoType') === SCREEN_SHARING_VIDEO_TYPE,
    );

    if (get(sharingConnection, 'ready')) {
      return get(sharingConnection, 'id');
    }

    return null;
  }, [connections]);

  const { video, stream: activeStream, user: activeUser } = useMemo(
    () => connections[focusedId] || {},
    [focusedId, connections],
  );

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.srcObject = video ? video.srcObject : null;
    }
  }, [video]);

  useEffect(() => {
    if (sharingConnectionId) {
      onPin({ id: sharingConnectionId });
    }
  }, [sharingConnectionId]);

  const noVideo = activeStream && !activeStream.hasVideo;

  const style = useMemo(
    () => ({
      left: coords.left,
      top: coords.top,
    }),
    [coords],
  );

  const subscriberClasses = useMemo(
    () => ({
      [styles.noVideo]: noVideo,
      [styles.collapsed]: collapsed,
      [styles.fullView]: !collapsed,
      [styles.isDragging]: isDragging,
      [styles.me]: isMe,
      [styles.fullScreenView]: fullScreenView,
    }),
    [noVideo, collapsed, isDragging, isMe, fullScreenView],
  );

  return (
    <div
      className={cn(styles.subscriber, subscriberClasses)}
      style={style}
      ref={drag}
    >
      {loading && <Loader className={styles.loader} />}
      <video ref={activeRef} autoPlay muted />
      {collapsed && <CollapsedBar activeUser={activeUser} />}
      {!collapsed && publisher && (
        <PublisherBtns publisher={publisher} disabled={loading} />
      )}
      {!collapsed && !loading && placeholder && !moreOneUserInRoom && (
        <Placeholder {...placeholder} />
      )}
    </div>
  );
}

ActiveSpeaker.propTypes = {
  publisher: ConnectionShape,
  placeholder: PropTypes.object,
  onPin: PropTypes.func,
  coords: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
  }),
};

export default ActiveSpeaker;



// WEBPACK FOOTER //
// ./src/components/VirtualVideoRoom/Components/ActiveSpeaker/index.jsx